"use client";

import Image from "next/image";
import { useMemo, type CSSProperties } from "react";

import { cn } from "@/lib/utils";

/**
 * Banner 6000 — BDU auxiliary page hero (bduedu.in `/student-support-services`).
 *
 * The static sibling of Banner 1025: same left overlay card and accreditation
 * bar, but a single background image instead of a carousel, and a plain H1
 * instead of title/subtitle + CTA. It is the hero every non-programme content
 * page on that site uses (its asset is literally `auxiliary_page_banner.png`).
 *
 * Pairs with Rich Text Editor 6001, which carries the body copy + lead rail.
 *
 * BREAKPOINTS — v1's `md:` is 768px, which is `tab:` here (this app remaps
 * `sm`/`md`/`lg` to 1080/1230/1430, so a literal `md:` would fire ~460px late).
 *
 * COLORS — v1 reaches these through Tailwind aliases this app does not define
 * (`bg-banner-gradient-blue`, `bg-bankGradient`, and a `white` that is #FFFFFF
 * rather than this theme's #F8F9FA). They are resolved to literal values and
 * exposed as CMS fields instead of re-introducing the aliases.
 */

/** `.bg-banner-gradient-blue` — same pair Banner 1025 defaults to. */
const OVERLAY_GRADIENT_START = "#13344F";
const OVERLAY_GRADIENT_END = "#77B6EB";
/** Solid overlay below 768px (v1 `bg-[#22405ADB]`). */
const OVERLAY_MOBILE = "#22405ADB";
/** `.bg-bankGradient` — an alias for plain white, despite the name. */
const BADGE_STRIP_BG = "#FFFFFF";

interface AccLogo {
  id?: string | number;
  image?: string;
  alt_image?: string;
  priority?: number;
  status?: boolean;
}

interface Banner6000Data {
  status?: boolean | number | string;
  /** Desktop background image. */
  image?: string;
  /** Optional; falls back to `image`. */
  mobile_image?: string;
  image_alt?: string;
  title?: string;
  title_color?: string;
  /**
   * Accreditation badges. Banner 1025 keeps these in `metadata_1` because its
   * `metadata` holds carousel slides; this variant has no slides, so the badges
   * take the primary group — which also satisfies `banner.metadata` being NOT NULL.
   */
  metadata?: AccLogo[];
  /** Overlay gradient from `tab:` up. */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Solid overlay below `tab:`. */
  background_color?: string;
  /** White strip behind the badges. */
  carousel_color?: string;
  /** Desktop hero height in px (mobile is 402). */
  hero_section_height?: number;
}

/** MySQL TINYINT(1) → boolean; Sequelize may return 0/1, "0"/"1", or boolean. */
function toBool(value: unknown, whenMissing: boolean): boolean {
  if (value == null) return whenMissing;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (["0", "false", "no", "off", ""].includes(t)) return false;
    if (["1", "true", "yes", "on"].includes(t)) return true;
  }
  return Boolean(value);
}

const Banner6000 = ({ data }: { data: any }) => {
  const sectionData: Banner6000Data | undefined = data?.data ?? data;

  const badges = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((b) => b?.status !== false && !!b?.image)
      .sort((a, b) => (Number(a?.priority) || 0) - (Number(b?.priority) || 0));
  }, [sectionData?.metadata]);

  if (!sectionData || !toBool(sectionData.status, true)) return null;

  const {
    image = "",
    mobile_image = "",
    image_alt = "",
    title = "",
    title_color = "#FFFFFF",
    gradient_color_1 = OVERLAY_GRADIENT_START,
    gradient_color_2 = OVERLAY_GRADIENT_END,
    background_color = OVERLAY_MOBILE,
    carousel_color = BADGE_STRIP_BG,
    hero_section_height = 418,
  } = sectionData;

  const desktopHeight = Number(hero_section_height) || 418;

  return (
    <section className="w-full">
      {/* v1 wraps the hero in a Card, hence the border + shadow. */}
      <div className="overflow-hidden rounded-none border border-slate-200 bg-[#FFFFFF] text-slate-950 shadow">
        {/* Desktop height is CMS-driven, so it arrives as a custom property the
            tab+ utility reads; mobile is v1's fixed 402px. */}
        <div
          className="relative h-[402px] w-full tab:h-[var(--b6000-h)]"
          style={{ ["--b6000-h" as string]: `${desktopHeight}px` }}
        >
          {/* v1 leaves `src=""` on pages with no banner configured and still
              paints the overlay card; next/image rejects an empty src, so the
              images are simply omitted in that case. */}
          {image ? (
            <Image
              src={image}
              alt={image_alt || title}
              width={800}
              height={desktopHeight}
              priority
              className="hidden size-full object-cover object-top tab:block"
            />
          ) : null}
          {mobile_image || image ? (
            <Image
              src={mobile_image || image}
              alt={image_alt || title}
              width={800}
              height={402}
              priority
              className="size-full object-cover object-top tab:hidden"
            />
          ) : null}

          {/* Full-bleed solid on mobile, 560px gradient card from tab up. */}
          <div className="absolute inset-0">
            <div
              className={cn(
                "flex h-full w-full rounded-none border-none",
                /* The gradient is CMS-driven, so it rides in on a custom
                   property that only the tab+ utility reads — below 768px v1
                   paints the solid colour on its own. */
                "tab:w-[560px] tab:bg-[image:var(--b6000-overlay)] tab:opacity-95",
              )}
              style={
                {
                  backgroundColor: background_color,
                  "--b6000-overlay": `linear-gradient(90deg, ${gradient_color_1}, ${gradient_color_2})`,
                } as CSSProperties
              }
            >
              <div className="mx-5 mb-8 mt-20 w-full p-0 tab:ml-11 tab:mt-[84px] tab:h-[260px] tab:w-[500px]">
                <div className="flex h-full flex-col items-center justify-between text-left tab:items-start tab:gap-y-9">
                  <h1
                    className="w-full text-center text-[32px]/[38px] font-bold tab:text-left tab:text-[48px]/[56px]"
                    style={{ color: title_color }}
                  >
                    {title}
                  </h1>

                  {badges.length > 0 && (
                    <div
                      className="flex h-10 w-auto rounded-[4px] tab:h-[62px] tab:rounded-[6px]"
                      style={{ backgroundColor: carousel_color }}
                    >
                      <div className="flex flex-row items-center justify-center">
                        {badges.map((badge, index) => (
                          <div
                            key={badge.id ?? index}
                            className="flex h-full items-center justify-center p-1.5 tab:max-w-[125px] tab:p-2.5"
                          >
                            <Image
                              src={badge.image as string}
                              alt={badge.alt_image || ""}
                              width={60}
                              height={24}
                              priority
                              className="h-7 w-full tab:h-[50px] tab:max-h-[125px]"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Banner6000;
